import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'

import { Badge, Checkbox, TextLink } from 'slab'
import { formatInvoiceDate } from 'utils/billing'
import { type EmployersTodo } from 'utils/web_api.schemas'

import { AchAuthorizationModal } from './modals/AchAuthorizationModal'

interface TodoItemProps {
  todo: EmployersTodo
  onMarkComplete: (id: string) => void
}

export function TodoItem ({ todo, onMarkComplete }: TodoItemProps) {
  const deadlineDate = todo.deadline ? formatInvoiceDate(new Date(todo.deadline)) : undefined
  const isCompleted = todo.status === 'completed'
  const [openModal, setOpenModal] = useState<string | null>(null)

  return (
    <div
      className='rounded-lg p-4 border flex items-start gap-3'
      style={{ borderColor: 'var(--color-stroke-light, #E5E5E5)' }}
    >
      <div className='flex-shrink-0 mt-0.5'>
        <Checkbox
          checked={isCompleted}
          onChange={() => {
            if (!isCompleted) {
              onMarkComplete(todo.id)
            }
          }}
          disabled={isCompleted}
        />
      </div>
      <div className='flex-1 min-w-0'>
        <p className='body-sm-bold mb-2.5'>{todo.title}</p>
        {isCompleted && todo.completed_at && todo.completed_by
          ? (
            <p className='body-sm text-gray-500'>
              <span className='body-sm-bold text-black'>{formatInvoiceDate(new Date(todo.completed_at))}</span> • <span className='body-sm-bold text-black'>{typeof todo.completed_by === 'string' ? todo.completed_by : (todo.completed_by as any)?.first_name + ' ' + (todo.completed_by as any)?.last_name}</span> marked as complete
            </p>
            )
          : (
            <div className='body-sm text-gray-500'>
              <ReactMarkdown
                components={{
                  a: ({ children, href, ...rest }) => {
                    if (href?.startsWith('#modal-')) {
                      const modalKey = href.replace('#modal-', '')
                      return (
                        <TextLink
                          {...rest}
                          href='#'
                          color='purple'
                          onClick={(e) => {
                            e.preventDefault()
                            setOpenModal(modalKey)
                          }}
                        >
                          {children}
                        </TextLink>
                      )
                    }
                    return (
                      <TextLink
                        {...rest}
                        href={href || '#'}
                        color='purple'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {children}
                      </TextLink>
                    )
                  },
                }}
                allowedElements={['a', 'p']}
              >
                {todo.body}
              </ReactMarkdown>
            </div>
            )}
      </div>
      {deadlineDate && !isCompleted && (
        <div className='flex-shrink-0'>
          <Badge leftIcon={AlertTriangle} type='caution'>
            Action required by {deadlineDate}
          </Badge>
        </div>
      )}
      {openModal === 'ach_authorization' && (
        <AchAuthorizationModal
          open={openModal === 'ach_authorization'}
          onOpenChange={(open) => {
            if (!open) {
              setOpenModal(null)
            }
          }}
        />
      )}
    </div>
  )
}
